import type Invoice from "../models/Invoice";

export interface InvoiceFilterCriteria {
  statusFilter: string[];
  accountingStatusFilter?: string[];
  companyFilter: string[];
  searchText: string;
  projectNumberSearch: string;
}

export type InvoiceStatus =
  | "credited"
  | "overpaid"
  | "paid"
  | "partially_paid"
  | "sent"
  | "sent_to_accounting"
  | "booked"
  | "finalized"
  | "credit_invoice"
  | "draft"
  | "discarded";

export type InvoiceAccountingStatus =
  | "manual_registered"
  | "creating"
  | "created"
  | "sync_error"
  | "not_registered"
  | "not_applicable";

/**
 * The single primary status shown for an invoice. Keep filters, list badges and
 * the detail banner on this shared precedence so the same invoice never appears
 * under a different status than the one visible to the user.
 */
export const getInvoiceStatus = (
  invoice?: Invoice,
  accountingSettlementStatus = invoice?.accountingSettlementStatus,
): InvoiceStatus => {
  if (!invoice) return "draft";
  if (invoice.lifecycleStatus === "discarded") return "discarded";
  if (invoice.isCredited) return "credited";
  // Settlement status is only populated from a verified accounting
  // integration. It may arrive through the detail read model before the list
  // summary has been refreshed, so do not require the denormalized mode here.
  if (accountingSettlementStatus === "credited") return "credited";
  if (accountingSettlementStatus === "overpaid") return "overpaid";
  if (accountingSettlementStatus === "settled") return "paid";
  if (accountingSettlementStatus === "partially_settled") {
    return "partially_paid";
  }
  if (invoice.isPaid) return "paid";
  if (invoice.isSent) return "sent";
  if (invoice.accountingSentAt) return "sent_to_accounting";
  if (invoice.lifecycleStatus === "finalized") return "finalized";
  // Legacy invoices did not have lifecycleStatus. isBooked is retained only as
  // a compatibility fallback and must not be interpreted as accounting state.
  if (!invoice.lifecycleStatus && invoice.isBooked) return "booked";
  if (invoice.isCreditInvoice) return "credit_invoice";
  return "draft";
};

/**
 * Apply the primary status / company / search / project-number filters to a
 * list of invoices.
 */
export const filterInvoices = (
  invoices: Invoice[],
  {
    statusFilter,
    accountingStatusFilter = [],
    companyFilter,
    searchText,
    projectNumberSearch,
  }: InvoiceFilterCriteria,
): Invoice[] =>
  invoices.filter((invoice) => {
    if (
      invoice.lifecycleStatus === "discarded" &&
      !statusFilter.includes("discarded")
    )
      return false;
    // Apply status filter
    if (statusFilter.length > 0) {
      if (!statusFilter.includes(getInvoiceStatus(invoice))) return false;
    }

    if (accountingStatusFilter.length > 0) {
      const accountingStatus = getInvoiceAccountingStatus(invoice);
      if (!accountingStatusFilter.includes(accountingStatus)) return false;
    }

    // Apply company filter
    if (companyFilter.length > 0) {
      const matchesCompany =
        companyFilter.includes(invoice.companyId) ||
        (invoice.invoicer?.id && companyFilter.includes(invoice.invoicer.id));
      if (!matchesCompany) return false;
    }

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const invoiceNumberStr = invoice.invoiceNumber
        ? String(invoice.invoiceNumber)
        : "";
      const customerName = invoice.invoicee?.isCompany
        ? invoice.invoicee?.name?.toLowerCase()
        : `${invoice.invoicee?.firstName} ${invoice.invoicee?.lastName}`.toLowerCase();

      const matchesSearch =
        invoiceNumberStr.toLowerCase().includes(searchLower) ||
        customerName?.includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Apply project number filter
    if (projectNumberSearch) {
      const projectSearchLower = projectNumberSearch.toLowerCase();
      const projectNumber = invoice.yourReference?.toLowerCase() || "";

      if (!projectNumber.includes(projectSearchLower)) return false;
    }

    return true;
  });

export const isInvoiceFinalized = (invoice?: Invoice): boolean =>
  Boolean(
    invoice &&
    (invoice.lifecycleStatus
      ? invoice.lifecycleStatus === "finalized"
      : invoice.isBooked),
  );

export const getInvoiceAccountingStatus = (
  invoice?: Invoice,
): InvoiceAccountingStatus => {
  if (!invoice || invoice.lifecycleStatus === "discarded")
    return "not_applicable";
  if (invoice.accountingState === "failed" || invoice.accountingLastErrorCode) {
    return "sync_error";
  }
  if (
    invoice.accountingState === "pending" ||
    invoice.accountingState === "submitted"
  ) {
    return "creating";
  }
  if (invoice.accountingState === "confirmed") {
    if (invoice.accountingMode !== "integration") return "manual_registered";
    // Payment belongs to the invoice lifecycle shown in the primary status
    // column. This status deliberately answers only whether the document has
    // been registered in accounting, so paid invoices do not show "Paid"
    // twice in the same row.
    return "created";
  }
  return isInvoiceFinalized(invoice) ? "not_registered" : "not_applicable";
};

/** UI eligibility mirrors the server guard; the transaction remains authoritative. */
export const canDiscardInvoice = (invoice?: Invoice): boolean =>
  Boolean(
    invoice &&
    isInvoiceDraft(invoice) &&
    !invoice.isBooked &&
    !invoice.finalizedAt &&
    !invoice.bookedAt &&
    !invoice.issuanceMode &&
    !invoice.isSent &&
    !invoice.sentAt &&
    !invoice.customerSentAt &&
    !invoice.accountingSentAt &&
    !invoice.isPaid &&
    !invoice.paidAmount &&
    !invoice.paidAt &&
    !invoice.isCredited &&
    !invoice.creditedAmount &&
    !invoice.creditedAt &&
    !invoice.deliveryHistory?.length &&
    (!invoice.accountingState || invoice.accountingState === "not_registered"),
  );

export const isInvoiceDraft = (invoice?: Invoice): boolean =>
  Boolean(
    invoice &&
    (invoice.lifecycleStatus
      ? invoice.lifecycleStatus === "draft"
      : !invoice.isBooked),
  );
